import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, OnDestroy, ChangeDetectorRef, HostListener, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { KonamiService } from '../../../service/konami.service';

export interface TextToken {
  text: string;
  type: 'character' | 'dialog' | 'suggestion' | 'error' | 'normal' | 'teleport' | 'typewriter' | 'warning' | 'meanwhile' | 'parentheses';
  tag?: string;
  tooltip?: string;
}

export interface Location {
  id: number;
  name: string;
  img_url: string;
}

export interface Sound {
  id: number;
  name: string;
  url: string;
}

export interface Backdrop {
  id: number;
  name: string;
  img_url: string;
  locations: Location[];
}

@Component({
  selector: 'app-rich-text-editor',
  imports: [CommonModule],
  templateUrl: './rich-text-editor.component.html',
  styleUrl: './rich-text-editor.component.less',
  standalone: true
})
export class RichTextEditorComponent implements AfterViewInit, OnDestroy {
  
  konamiService = inject(KonamiService);

  @Input() set content(value: string) {
    const previousContent = this._content;
    this._content = value;
    
    // Only process if the view is initialized and content actually changed
    if (this.textareaRef && this.textareaRef.nativeElement && this.highlightDivRef && this.highlightDivRef.nativeElement) {
      if (value !== previousContent) {
        this.textareaRef.nativeElement.value = value || '';
        this.lastContent = value || '';
        this.pendingContent = value || '';
        
        if (value) {
          this.updateHighlightingImmediate(value);
          this.scheduleTokenCalculation();
        } else {
          this.updateHighlightingImmediate('');
          this.lastProcessedContent = '';
          this.lastProcessedHtml = '';
          this.lastCursorPosition = 0;
        }
      }
    }
  }
  get content(): string {
    return this._content;
  }
  
  @Input() set characters(value: Array<{tag: string, name: string}>) {
    this._characters = value;
    // Trigger revalidation when characters change
    if (this.textareaRef && this.textareaRef.nativeElement) {
      this.scheduleTokenCalculation();
    }
  }
  get characters(): Array<{tag: string, name: string}> {
    return this._characters;
  }
  
  @Input() set suggestions(value: Array<{tag: string, description: string, examples: string[]}>) {
    this._suggestions = value;
    // Trigger revalidation when suggestions change
    if (this.textareaRef && this.textareaRef.nativeElement) {
      this.scheduleTokenCalculation();
    }
  }
  get suggestions(): Array<{tag: string, description: string, examples: string[]}> {
    return this._suggestions;
  }
  
  @Input() set sounds(value: Sound[]) {
    this._sounds = value;
    // Trigger revalidation when sounds change
    if (this.textareaRef && this.textareaRef.nativeElement) {
      this.scheduleTokenCalculation();
    }
  }
  get sounds(): Sound[] {
    return this._sounds;
  }
  
  @Input() set universal_locations(value: Location[]) {
    this._universal_locations = value;
    // Trigger revalidation when universal locations change
    if (this.textareaRef && this.textareaRef.nativeElement) {
      this.scheduleTokenCalculation();
    }
  }
  get universal_locations(): Location[] {
    return this._universal_locations;
  }
  
  @Input() set backdrop(value: Backdrop | null) {
    this._backdrop = value;
    // Trigger revalidation when backdrop changes
    if (this.textareaRef && this.textareaRef.nativeElement) {
      this.scheduleTokenCalculation();
    }
  }
  get backdrop(): Backdrop | null {
    return this._backdrop;
  }
  
  @Input() set scriptTitle(value: string) {
    this._scriptTitle = value;
    // Trigger revalidation when script title changes
    if (this.textareaRef && this.textareaRef.nativeElement) {
      this.scheduleTokenCalculation();
    }
  }
  get scriptTitle(): string {
    return this._scriptTitle;
  }
  
  @Input() readonly = false;

  @Output() contentChange = new EventEmitter<string>();
  
  @ViewChild('textarea') textareaRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('highlightDiv') highlightDivRef!: ElementRef<HTMLDivElement>;
  @ViewChild('lineNumbersDiv') lineNumbersDivRef!: ElementRef<HTMLDivElement>;
  @ViewChild('richTextEditorContainer') richTextEditorContainer!: ElementRef<HTMLDivElement>;
  
  private _content: string = '';
  private _characters: Array<{tag: string, name: string}> = [];
  private _suggestions: Array<{tag: string, description: string, examples: string[]}> = [];
  private _scriptTitle: string = '';
  private isProcessing = false;
  private lastContent = '';
  private debounceTimer: any = null;
  private lastTokenCalculation = 0;
  private readonly TOKEN_CALCULATION_INTERVAL = 250; // 0.5 seconds
  private pendingContent = '';
  private isWaitingForTokens = false;
  
  // Highlighting preservation
  private lastProcessedContent = '';
  private lastProcessedHtml = '';
  private lastCursorPosition = 0;
  
  // Performance debugging
  private debugEnabled = true;
  private performanceStats: { [key: string]: { totalTime: number, callCount: number, lastCallTime: number } } = {};
  
  // Preview mode properties
  isPreviewMode = false;
  originalContent = '';
  previewContent = '';
  suggestionReplacements: Map<string, {tag: string, description: string, example?: string}> = new Map();
  
  // Error and warning tracking
  errors: Array<{text: string, tooltip: string, line?: number}> = [];
  warnings: Array<{text: string, tooltip: string, line?: number}> = [];
  
  // Track previous line type for meanwhile validation
  private previousLineType: 'meanwhile' | 'dialog' | 'other' = 'other';
  private previousCharacterTag: string | null = null;
  
  // Track consecutive TYPEWRITER lines
  private consecutiveTypewriterLines = 0;
  private readonly MAX_CONSECUTIVE_TYPEWRITER = 1;
  
  // Profanity filter
  private readonly EXPLICIT_WORDS = [
    'fuck', 'fucking', 'fucked', 'fucker', 'fucks',
    'shit', 'shitting', 'shitted', 'shitter', 'shits',
    'bitch', 'bitches', 'bitching', 'bitched',
    'ass', 'asses', 'asshole', 'assholes',
    'dick', 'dicks', 'dickhead', 'dickheads',
    'pussy', 'pussies',
    'cock', 'cocks',
    'whore', 'whores',
    'slut', 'sluts',
    'cunt', 'cunts',
    "nigga", "niggas", "nigger", "niggers",
    "asshole", "assholes",
  ];
  
  // Track used characters and suggestions for unused element warnings
  private usedCharacters: Set<string> = new Set();
  private usedSuggestions: Set<string> = new Set();
  private suggestionsUsedInActions: Set<string> = new Set();
  
  // Track character first appearances for Backstage validation
  private characterFirstAppearances: Map<string, string> = new Map();
  
  // Track character speaking line counts
  private characterSpeakingLines: Map<string, number> = new Map();
  
  // Track non-dialogue lines for missing dialogue validation
  private nonDialogueLines: number = 0;
  private actionOnlyLines: number = 0;
  private totalLines: number = 0;
  
  // Track music commands
  private hasMusicCommands: boolean = false;
  
  // Script length tracking
  private totalScriptLength = 0;
  private readonly MAX_SCRIPT_LENGTH = 18000;
  private readonly MAX_DIALOGUE_LENGTH = 160;
  
  private _backdrop: Backdrop | null = null;
  private _sounds: Sound[] = [];
  private _universal_locations: Location[] = [];
  

  constructor(private cdr: ChangeDetectorRef) {}
  
  // Performance debugging utilities
  private debugStart(functionName: string): number {
    if (!this.debugEnabled) return 0;
    return performance.now();
  }
  
  private debugEnd(functionName: string, startTime: number) {
    if (!this.debugEnabled) return;
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (!this.performanceStats[functionName]) {
      this.performanceStats[functionName] = { totalTime: 0, callCount: 0, lastCallTime: 0 };
    }
    
    this.performanceStats[functionName].totalTime += duration;
    this.performanceStats[functionName].callCount += 1;
    this.performanceStats[functionName].lastCallTime = duration;
    
    // console.log(`🔍 ${functionName}: ${duration.toFixed(2)}ms (Total: ${this.performanceStats[functionName].totalTime.toFixed(2)}ms, Calls: ${this.performanceStats[functionName].callCount})`);
  }
  
  private debugFunction<T>(functionName: string, fn: () => T): T {
    const startTime = this.debugStart(functionName);
    try {
      const result = fn();
      this.debugEnd(functionName, startTime);
      return result;
    } catch (error) {
      this.debugEnd(functionName, startTime);
      throw error;
    }
  }
  
  // Method to print all performance stats
  public printPerformanceStats() {
    if (!this.debugEnabled) return;
    
    let x = ""

    x += '📊 PERFORMANCE STATISTICS:\n';
    x += '========================\n';
    
    const sortedStats = Object.entries(this.performanceStats)
      .sort(([,a], [,b]) => b.totalTime - a.totalTime);
    
    sortedStats.forEach(([functionName, stats]) => {
      const avgTime = stats.callCount > 0 ? stats.totalTime / stats.callCount : 0;
      x += `${functionName}:\n`;
      x += `  Total time: ${stats.totalTime.toFixed(2)}ms\n`;
      x += `  Call count: ${stats.callCount}\n`;
      x += `  Average time: ${avgTime.toFixed(2)}ms\n`;
      x += `  Last call: ${stats.lastCallTime.toFixed(2)}ms\n`;
      x += '\n';
    });
    // console.log(x)
  }
  
  // Method to clear performance stats
  public clearPerformanceStats() {
    this.performanceStats = {};
    // console.log('🧹 Performance stats cleared');
  }
  
  // Method to toggle debugging
  public toggleDebug() {
    this.debugEnabled = !this.debugEnabled;
    // console.log(`🔧 Debug ${this.debugEnabled ? 'enabled' : 'disabled'}`);
  }
  
  // Method to force immediate token calculation (for testing)
  public forceTokenCalculation() {
    // console.log('🚀 Forcing immediate token calculation...');
    this.lastTokenCalculation = 0; // Reset the timer
    this.calculateTokens();
  }

  // Method to force immediate line number update (for testing)
  public forceLineNumberUpdate() {
    // console.log('🔢 Forcing immediate line number update...');
    this.updateLineNumbers();
  }
  
  // Method to clear highlighting preservation state
  public clearHighlightingState() {
    this.lastProcessedContent = '';
    this.lastProcessedHtml = '';
    this.lastCursorPosition = 0;
    // console.log('🧹 Highlighting preservation state cleared');
  }
  
  ngAfterViewInit() {
    this.setupEditor();
    
    (window as any).editorPerformance = this.performanceStats;

    // Make the component globally accessible for debugging
    (window as any).richTextEditor = this;
    // console.log('🔧 Rich Text Editor debugging enabled. Use these commands:');
    // console.log('  richTextEditor.printPerformanceStats() - Show performance statistics');
    // console.log('  richTextEditor.clearPerformanceStats() - Clear performance statistics');
    // console.log('  richTextEditor.toggleDebug() - Toggle debug logging');
    // console.log('  richTextEditor.forceTokenCalculation() - Force immediate token calculation');
    // console.log('  richTextEditor.forceLineNumberUpdate() - Force immediate line number update');
    // console.log('  richTextEditor.clearHighlightingState() - Clear highlighting preservation state');
  }
  
  ngOnDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private setupEditor() {
    const textarea = this.textareaRef.nativeElement;
    const highlightDiv = this.highlightDivRef.nativeElement;
    const lineNumbersDiv = this.lineNumbersDivRef?.nativeElement;
    
    // Set initial content and process it if it exists
    if (this.content) {
      this.lastContent = this.content;
      this.originalContent = this.content;
      this.pendingContent = this.content;
      textarea.value = this.content;
      
      // Process the initial content to show highlighting after a short delay
      // to ensure all inputs are properly initialized
      requestAnimationFrame(() => {
        if (this.content) {
          this.calculateTokens();
        }
      });
    }
    
    // Sync scroll positions
    textarea.addEventListener('scroll', () => {
      highlightDiv.scrollTop = textarea.scrollTop;
      highlightDiv.scrollLeft = textarea.scrollLeft;
      
      if (lineNumbersDiv) {
        lineNumbersDiv.scrollTop = textarea.scrollTop;
      }
    });
  }
  
  private scheduleTokenCalculation() {
    const now = Date.now();
    const timeSinceLastCalculation = now - this.lastTokenCalculation;
    
    if (timeSinceLastCalculation >= this.TOKEN_CALCULATION_INTERVAL) {
      // Calculate tokens immediately if enough time has passed
      this.calculateTokens();
    } else {
      // Set up debounced calculation
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      const remainingTime = this.TOKEN_CALCULATION_INTERVAL - timeSinceLastCalculation;
      this.debounceTimer = setTimeout(() => {
        this.calculateTokens();
      }, remainingTime);
      
      this.isWaitingForTokens = true;
    }
  }
  
  onTextareaInput() {
    return this.debugFunction('onTextareaInput', () => {
      // If in preview mode, disable it when user starts typing
      if (this.isPreviewMode) {
        this.disablePreviewMode();
      }
      
      if (this.isProcessing) return;
      
      this.processInput();
    });
  }
  
  private processInput() {
    return this.debugFunction('processInput', () => {
      const textarea = this.textareaRef.nativeElement;
      const currentContent = textarea.value;
      
      // Only process if content actually changed
      if (currentContent !== this.lastContent) {
        this.isProcessing = true;
        
        // Store the pending content for token calculation
        this.pendingContent = currentContent;
        
        // Immediately show the content without any highlighting (for responsive typing)
        this.updateHighlightingImmediate(currentContent);
        
        // Schedule token calculation using the new method
        this.scheduleTokenCalculation();
        
        // Update last content and emit
        this.lastContent = currentContent;
        this.originalContent = currentContent;
        this.contentChange.emit(currentContent);
        
        this.isProcessing = false;
        this.cdr.detectChanges();
      }
    });
  }
  
  private updateHighlighting() {
    // This method is now deprecated in favor of calculateTokens
    // For backward compatibility, call calculateTokens
    this.calculateTokens();
  }

  private updateHighlightingImmediate(content: string) {
    return this.debugFunction('updateHighlightingImmediate', () => {
      // Check if the view is properly initialized
      if (!this.textareaRef || !this.highlightDivRef || !this.textareaRef.nativeElement || !this.highlightDivRef.nativeElement) {
        return;
      }
      
      const textarea = this.textareaRef.nativeElement;
      const highlightDiv = this.highlightDivRef.nativeElement;
      const currentCursorPosition = textarea.selectionStart;
      
      // If we don't have any previous highlighting, just show the content without highlighting
      if (!this.lastProcessedHtml || this.lastProcessedContent === '') {
        const escapedContent = this.escapeHtml(content);
        const formattedContent = escapedContent.replace(/\n/g, '<br>');
        highlightDiv.innerHTML = formattedContent;
        this.lastProcessedContent = content;
        this.lastProcessedHtml = formattedContent;
        this.lastCursorPosition = currentCursorPosition;
      } else {
        // We have previous highlighting, try to preserve it
        this.updateContentPreservingHighlighting(content, currentCursorPosition);
      }
      
      // Sync scroll position
      highlightDiv.scrollTop = textarea.scrollTop;
      highlightDiv.scrollLeft = textarea.scrollLeft;
      
      // Update line numbers
      this.updateLineNumbers();
    });
  }
  
  private updateContentPreservingHighlighting(newContent: string, cursorPosition: number) {
    const highlightDiv = this.highlightDivRef.nativeElement;
    
    // Simple approach: if the content is just an addition at the end, preserve existing highlighting
    if (newContent.startsWith(this.lastProcessedContent)) {
      // Content was added at the end, preserve existing highlighting
      const addedContent = newContent.substring(this.lastProcessedContent.length);
      const escapedAddedContent = this.escapeHtml(addedContent);
      const formattedAddedContent = escapedAddedContent.replace(/\n/g, '<br>');
      
      // Append the new content without highlighting
      highlightDiv.innerHTML = this.lastProcessedHtml + formattedAddedContent;
      this.lastProcessedContent = newContent;
      this.lastProcessedHtml = highlightDiv.innerHTML;
      this.lastCursorPosition = cursorPosition;
    } else if (this.lastProcessedContent.startsWith(newContent)) {
      // Content was deleted from the end, preserve existing highlighting for remaining content
      const remainingContent = newContent;
      const escapedRemainingContent = this.escapeHtml(remainingContent);
      const formattedRemainingContent = escapedRemainingContent.replace(/\n/g, '<br>');
      
      highlightDiv.innerHTML = formattedRemainingContent;
      this.lastProcessedContent = newContent;
      this.lastProcessedHtml = highlightDiv.innerHTML;
      this.lastCursorPosition = cursorPosition;
    } else {
      // Complex change (insertion/deletion in middle), fall back to showing without highlighting
      const escapedContent = this.escapeHtml(newContent);
      const formattedContent = escapedContent.replace(/\n/g, '<br>');
      highlightDiv.innerHTML = formattedContent;
      this.lastProcessedContent = newContent;
      this.lastProcessedHtml = formattedContent;
      this.lastCursorPosition = cursorPosition;
    }
  }
  
  private calculateTokens() {
    return this.debugFunction('calculateTokens', () => {
      // Check if the view is properly initialized
      if (!this.textareaRef || !this.highlightDivRef || !this.textareaRef.nativeElement || !this.highlightDivRef.nativeElement) {
        return;
      }
      
      // Clear any pending debounced calculation
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      
      // Update the last calculation time
      this.lastTokenCalculation = Date.now();
      this.isWaitingForTokens = false;
      
      // Use the pending content for token calculation
      const content = this.pendingContent;
      
      // Clear previous errors and warnings
      this.errors = [];
      this.warnings = [];
      
      // Reset tracking sets
      this.usedCharacters.clear();
      this.usedSuggestions.clear();
      this.suggestionsUsedInActions.clear();
      this.characterFirstAppearances.clear();
      this.characterSpeakingLines.clear();
      this.nonDialogueLines = 0;
      this.actionOnlyLines = 0;
      this.totalLines = 0;
      this.hasMusicCommands = false;
      this.totalScriptLength = content.length;
      
      // Validate meta elements (characters and suggestions) first
      this.validateMetaElements();
      
      // Check script metadata (title)
      this.checkScriptMetadata();
      
      // Preserve meta validation results before tokenization resets state
      const metaErrors = this.errors.slice();
      const metaWarnings = this.warnings.slice();
      const hasMetaErrors = metaErrors.length > 0;
      
      // Process content and update highlighting div
      const processedHtml = this.processContent(content);
      const highlightDiv = this.highlightDivRef.nativeElement;
      highlightDiv.innerHTML = processedHtml;
      
      // Update tracking properties for highlighting preservation
      this.lastProcessedContent = content;
      this.lastProcessedHtml = processedHtml;
      this.lastCursorPosition = this.textareaRef.nativeElement.selectionStart;
      
      // Merge back meta validation results that were collected before tokenization
      if (metaErrors.length > 0) {
        this.errors.push(...metaErrors);
      }
      if (metaWarnings.length > 0) {
        this.warnings.push(...metaWarnings);
      }
      
      // Only check for unused elements and overall requirements if no meta errors
      if (!hasMetaErrors) {
        // Check for unused elements
        this.checkUnusedElements();
        
        // Check overall script requirements
        this.checkOverallScriptRequirements();
      }
      
      // Sync scroll position
      const textarea = this.textareaRef.nativeElement;
      highlightDiv.scrollTop = textarea.scrollTop;
      highlightDiv.scrollLeft = textarea.scrollLeft;
      
      // Update line numbers
      this.updateLineNumbers();
      
      // Trigger change detection to update the error/warning lists
      this.cdr.detectChanges();
    });
  }
  
  private updateLineNumbers() {
    return this.debugFunction('updateLineNumbers', () => {
      // Check if the view is properly initialized
      if (!this.lineNumbersDivRef || !this.textareaRef || !this.lineNumbersDivRef.nativeElement || !this.textareaRef.nativeElement) {
        return;
      }
      
      const textarea = this.textareaRef.nativeElement;
      const lineNumbersDiv = this.lineNumbersDivRef.nativeElement;
      const content = textarea.value;
      
      // Count lines in content
      const lines = content.split('\n');
      const lineCount = lines.length;
      
      // Calculate line heights and wrapping
      const lineHeights = this.calculateLineHeights(textarea, lines);
      
      // Generate line numbers HTML with proper spacing
      let lineNumbersHtml = '';
      for (let i = 0; i < lineCount; i++) {
        const lineNumber = i + 1;
        const lineHeight = lineHeights[i];
        const height = lineHeight > 1 ? `${lineHeight * 1.5}em` : '1.5em';
        lineNumbersHtml += `<div class="line-number" style="height: ${height}; min-height: ${height};">${lineNumber}</div>`;
      }
      
      // Update line numbers container
      lineNumbersDiv.innerHTML = lineNumbersHtml;
      
      // Sync scroll position with textarea
      lineNumbersDiv.scrollTop = textarea.scrollTop;
    });
  }

  private calculateLineHeights(textarea: HTMLTextAreaElement, lines: string[]): number[] {
    const lineHeights: number[] = [];
    const textareaStyle = window.getComputedStyle(textarea);
    const fontSize = parseFloat(textareaStyle.fontSize);
    const fontFamily = textareaStyle.fontFamily;
    const padding = parseFloat(textareaStyle.paddingLeft) + parseFloat(textareaStyle.paddingRight);
    const availableWidth = textarea.clientWidth - padding;
    
    // Create a temporary element to measure text width
    const tempElement = document.createElement('div');
    tempElement.style.position = 'absolute';
    tempElement.style.visibility = 'hidden';
    tempElement.style.whiteSpace = 'pre-wrap';
    tempElement.style.wordWrap = 'break-word';
    tempElement.style.fontSize = fontSize + 'px';
    tempElement.style.fontFamily = fontFamily;
    tempElement.style.lineHeight = '1.5';
    tempElement.style.width = availableWidth + 'px';
    tempElement.style.boxSizing = 'border-box';
    tempElement.style.padding = '0';
    tempElement.style.margin = '0';
    tempElement.style.border = 'none';
    document.body.appendChild(tempElement);
    
    try {
      for (const line of lines) {
        tempElement.textContent = line;
        
        // Get the actual height of the element after text wrapping
        const elementHeight = tempElement.offsetHeight;
        const singleLineHeight = fontSize * 1.5; // 1.5em line height
        const linesNeeded = Math.ceil(elementHeight / singleLineHeight);
        
        lineHeights.push(Math.max(1, linesNeeded));
      }
    } finally {
      document.body.removeChild(tempElement);
    }
    
    return lineHeights;
  }
  
  private validateMetaElements() {
    return this.debugFunction('validateMetaElements', () => {
      // Validate character uniqueness
      for (let i = 0; i < this.characters.length; i++) {
        for (let j = i + 1; j < this.characters.length; j++) {
          const char1 = this.characters[i].tag;
          const char2 = this.characters[j].tag;
          
          if (char1.toLowerCase().includes(char2.toLowerCase()) || char2.toLowerCase().includes(char1.toLowerCase())) {
            this.errors.push({
              text: `${char1} and ${char2}`,
              tooltip: `${char1} is not a unique character name. Names must be totally different from each other! (${char2})`
            });
          }
        }
      }
      
      // Check for reserved character names
      for (const character of this.characters) {
        if (character.tag.toLowerCase() === 'typewriter') {
          this.errors.push({
            text: character.tag,
            tooltip: 'You do not need to define Typewriter as a character'
          });
        }
      }
      
      // Validate character name lengths and symbolic tags
      for (const character of this.characters) {
        const nameLength = character.name.length;
        
        // Check for colon in character tag
        if (character.tag.includes(':')) {
          this.errors.push({
            text: character.tag,
            tooltip: `Character tag "${character.tag}" cannot contain the colon (:) character.`
          });
        }
        
        // Check for colon in character name
        if (character.name.includes(':')) {
          this.errors.push({
            text: character.name,
            tooltip: `Character name "${character.name}" cannot contain the colon (:) character.`
          });
        }
        
        if (nameLength > 30) {
          this.errors.push({
            text: character.name,
            tooltip: `Character name "${character.name}" is too long (${nameLength} characters). Character names should be 30 characters or less for better readability.`
          });
        } else if (nameLength > 20) {
          this.warnings.push({
            text: character.name,
            tooltip: `Character name "${character.name}" is quite long (${nameLength} characters). Consider shortening it to under 20 characters for better readability.`
          });
        }
        
        // Check for symbolic tags (2 chars + digits)
        if (this.isSymbolicTag(character.tag)) {
          this.warnings.push({
            text: character.tag,
            tooltip: `Character tag "${character.tag}" appears to be symbolic (like N1, N2, A1, A2). Consider using more descriptive character names to make your script clearer.`
          });
        }
      }
      
      // Validate suggestion count
      const suggestionCount = this.suggestions.length;
      if (suggestionCount < 3) {
        this.errors.push({
          text: 'Too few suggestions',
          tooltip: `Script has only ${suggestionCount} suggestions. You need to define many suggestions so that there is a lot of audience engagement. The max is 12.`
        });
      } else if (suggestionCount < 6) {
        this.warnings.push({
          text: 'Few suggestions',
          tooltip: `Script has only ${suggestionCount} suggestions. Consider adding more suggestions (6+) for better audience engagement.`
        });
      }
      
      // Validate suggestion examples and content
      for (const suggestion of this.suggestions) {
        const exampleCount = suggestion.examples ? suggestion.examples.length : 0;
        if (exampleCount < 3) {
          this.errors.push({
            text: suggestion.tag,
            tooltip: `Suggestion "${suggestion.tag}" has only ${exampleCount} examples. Each suggestion needs at least 3 examples.`
          });
        }
        
        // Check for colon in suggestion description
        if (suggestion.description.includes(':')) {
          this.errors.push({
            text: suggestion.tag,
            tooltip: `Suggestion description for "${suggestion.tag}" cannot contain the colon (:) character.`
          });
        }
        
        // Check for colon in suggestion examples
        if (suggestion.examples) {
          for (const example of suggestion.examples) {
            if (example.includes(':')) {
              this.errors.push({
                text: suggestion.tag,
                tooltip: `Suggestion "${suggestion.tag}" has an example containing a colon (:). Examples cannot contain the colon character.`
              });
              break; // Only show one error per suggestion
            }
          }
        }
        
        // Check for inappropriate content in suggestion tag or description
        const tagLower = suggestion.tag.toLowerCase();
        const descLower = suggestion.description.toLowerCase();
        
        if (tagLower.includes('insult') || descLower.includes('insult')) {
          this.warnings.push({
            text: suggestion.tag,
            tooltip: `Suggestions can't prompt the audience for direct insults or derogatory terms. The suggestion "${suggestion.tag}" will likely result in your script being rejected or this suggestion being removed.`
          });
        }
        
        // Check for inappropriate content in suggestion examples
        if (suggestion.examples) {
          for (const example of suggestion.examples) {
            const exampleLower = example.toLowerCase();
            if (exampleLower.includes('insult')) {
              this.warnings.push({
                text: suggestion.tag,
                tooltip: `Suggestion "${suggestion.tag}" has an example containing "insult". Examples should not contain inappropriate content.`
              });
              break; // Only show one warning per suggestion
            }
          }
        }
        
        // Check for symbolic tags (2 chars + digits)
        if (this.isSymbolicTag(suggestion.tag)) {
          this.warnings.push({
            text: suggestion.tag,
            tooltip: `Suggestion tag "${suggestion.tag}" appears to be symbolic (like N1, N2, A1, A2). Consider using more descriptive suggestion tags to make your script clearer.`
          });
        }
      }
      
      // Validate suggestion uniqueness
      for (let i = 0; i < this.suggestions.length; i++) {
        for (let j = i + 1; j < this.suggestions.length; j++) {
          const sugg1 = this.suggestions[i].tag;
          const sugg2 = this.suggestions[j].tag;
          
          if (sugg1.toLowerCase().includes(sugg2.toLowerCase()) || sugg2.toLowerCase().includes(sugg1.toLowerCase())) {
            this.errors.push({
              text: `${sugg1} and ${sugg2}`,
              tooltip: `${sugg1} is not a unique suggestion tag. Tags must be totally different from each other! (${sugg2})`
            });
          }
        }
      }
    });
  }
  
  private checkUnusedElements() {
    return this.debugFunction('checkUnusedElements', () => {
      // Check for unused characters
      for (const character of this.characters) {
        if (!this.usedCharacters.has(character.tag)) {
          this.warnings.push({
            text: character.tag,
            tooltip: `Character ${character.tag} is not used anywhere in the script`
          });
        }
      }
      
      // Check for unused suggestions
      for (const suggestion of this.suggestions) {
        if (!this.usedSuggestions.has(suggestion.tag)) {
          this.warnings.push({
            text: suggestion.tag,
            tooltip: `Audience suggestion ${suggestion.tag} is not used anywhere in the script`
          });
        }
      }
      
      // Check for suggestions that are used but never spoken (only in actions)
      for (const suggestion of this.suggestions) {
        if (this.usedSuggestions.has(suggestion.tag) && this.suggestionsUsedInActions.has(suggestion.tag)) {
          // This suggestion is used in actions, but we need to check if it's also used in dialogue
          // For now, we'll assume if it's in actions, it might not be spoken
          this.warnings.push({
            text: suggestion.tag,
            tooltip: `Suggestion "${suggestion.tag}" is not used in dialogue. Make sure suggestions are actually spoken out loud so that the audience can hear them.`
          });
        }
      }
    });
  }
  
  private checkOverallScriptRequirements() {
    return this.debugFunction('checkOverallScriptRequirements', () => {
      // Only validate dialogue requirement when there is actual content
      const hasAnyContent = !!this.pendingContent && this.pendingContent.trim().length > 0;
      // Check if there are any dialogue lines
      if (hasAnyContent && this.usedCharacters.size === 0) {
        this.errors.push({
          text: 'No dialogue',
          tooltip: 'No dialogue lines detected. You need at least 1 line where a character says something'
        });
      }
      
      // Check script length
      if (this.totalScriptLength > this.MAX_SCRIPT_LENGTH) {
        this.errors.push({
          text: 'Script too long',
          tooltip: `Your script is too long! The maximum script length is ${this.MAX_SCRIPT_LENGTH} characters. Shorten it by at least ${this.totalScriptLength - this.MAX_SCRIPT_LENGTH} characters`
        });
      }
      
      // Check if all characters start Backstage
      this.checkAllCharactersStartBackstage();
      
      // Check character speaking lines
      this.checkCharacterSpeakingLines();
      
      // Check for missing dialogue
      this.checkMissingDialogue();
      
      // Check for music commands
      this.checkMusicCommands();
      
    });
  }
  
  private checkScriptMetadata() {
    return this.debugFunction('checkScriptMetadata', () => {
      // Check script title
      if (!this.scriptTitle || this.scriptTitle.trim().length === 0) {
        this.errors.push({
          text: 'Missing title',
          tooltip: 'Script title is required'
        });
      }
      
    });
  }
  
  // Preview mode methods
  togglePreviewMode() {
    if (this.isPreviewMode) {
      this.disablePreviewMode();
    } else {
      this.enablePreviewMode();
    }
  }
  
  private enablePreviewMode() {
    this.isPreviewMode = true;
    this.originalContent = this.textareaRef.nativeElement.value;
    
    // Generate replacements for all suggestions
    this.generateSuggestionReplacements();
    
    // Create preview content by processing tokens
    this.previewContent = this.createPreviewContent(this.originalContent);
    
    // Update textarea and highlighting
    this.textareaRef.nativeElement.value = this.previewContent;
    this.textareaRef.nativeElement.readOnly = true; // Make read-only during preview
    this.lastContent = this.previewContent;
    this.pendingContent = this.previewContent; // Update pending content for token calculation
    this.updateHighlighting();
    this.cdr.detectChanges();
  }
  
  private disablePreviewMode() {
    this.isPreviewMode = false;
    
    // Restore original content
    this.textareaRef.nativeElement.value = this.originalContent;
    this.textareaRef.nativeElement.readOnly = false; // Re-enable editing
    this.lastContent = this.originalContent;
    this.pendingContent = this.originalContent; // Update pending content for token calculation
    this.updateHighlighting();
    this.cdr.detectChanges();
  }
  
  private generateSuggestionReplacements() {
    this.suggestionReplacements.clear();
    
    // Generate a replacement for each suggestion
    for (const suggestion of this.suggestions) {
      if (suggestion.examples && suggestion.examples.length > 0) {
        // Use a random example for each suggestion
        const example = suggestion.examples[Math.floor(Math.random() * suggestion.examples.length)];
        this.suggestionReplacements.set(suggestion.tag, { 
          tag: suggestion.tag, 
          description: suggestion.description,
          example: example 
        });
      }
    }
  }
  
  private createPreviewContent(content: string): string {
    // Tokenize the original content
    const tokens = this.tokenizeContent(content);
    
    // Process tokens to replace suggestion tokens with their examples
    const processedTokens = this.processTokensForPreview(tokens);
    
    // Convert tokens back to string
    return processedTokens.map(token => token.text).join('');
  }
  
  private processTokensForPreview(tokens: TextToken[]): TextToken[] {
    return tokens.map(token => {
      if (token.type === 'suggestion' && token.tag) {
        const replacement = this.suggestionReplacements.get(token.tag);
        if (replacement && replacement.example) {
          // Create a new token with the example text but keep the suggestion type and tag
          return {
            ...token,
            text: replacement.example
          };
        }
      }
      return token;
    });
  }
  
  // Legacy method - kept for backward compatibility with tests
  private replaceSuggestionsWithExamples(content: string): string {
    let result = content;
    this.suggestionReplacements.clear();
    
    // Replace each suggestion tag with a random example
    for (const suggestion of this.suggestions) {
      if (suggestion.examples && suggestion.examples.length > 0) {
        // Use the first example for consistency
        const example = suggestion.examples[Math.floor(Math.random() * suggestion.examples.length)];
        const regex = new RegExp(`\\b${suggestion.tag}\\b`, 'g');
        result = result.replace(regex, example);
        this.suggestionReplacements.set(example, { tag: suggestion.tag, description: suggestion.description });
      }
    }
    
    return result;
  }
  
  onTextareaScroll() {
    const textarea = this.textareaRef.nativeElement;
    const highlightDiv = this.highlightDivRef.nativeElement;
    const lineNumbersDiv = this.lineNumbersDivRef?.nativeElement;
    
    highlightDiv.scrollTop = textarea.scrollTop;
    highlightDiv.scrollLeft = textarea.scrollLeft;
    
    if (lineNumbersDiv) {
      lineNumbersDiv.scrollTop = textarea.scrollTop;
    }
  }
  
  onTextareaKeydown(event: KeyboardEvent) {
    // Handle special keys if needed
    if (event.key === 'Tab') {
      event.preventDefault();
      this.insertTextAtCursor('\t');
    }
  }
  
  onTextareaPaste(event: ClipboardEvent) {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') || '';
    this.insertTextAtCursor(text);
  }
  
  private insertTextAtCursor(text: string) {
    const textarea = this.textareaRef.nativeElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;
    
    const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
    textarea.value = newValue;
    
    // Set cursor position after inserted text
    const newCursorPos = start + text.length;
    textarea.setSelectionRange(newCursorPos, newCursorPos);
    
    // Trigger input event
    this.onTextareaInput();
    
    // Focus back to textarea
    textarea.focus();
  }
  

  @HostListener('click', ['$event'])
  onContainerClick(event: Event) {
    // Ensure textarea gets focus when clicking anywhere in the container
    const textarea = this.textareaRef.nativeElement;
    if (event.target !== textarea) {
      textarea.focus();
    }
  }
  
  private processContent(content: string): string {
    return this.debugFunction('processContent', () => {
      const tokens = this.tokenizeContent(content);
      return tokens.map(token => this.renderToken(token)).join('');
    });
  }
  
  private tokenizeContent(content: string): TextToken[] {
    return this.debugFunction('tokenizeContent', () => {
      const tokens: TextToken[] = [];
      // Ensure a clean slate when tokenizing ad-hoc (used heavily in tests)
      this.errors = [];
      this.warnings = [];
      this.usedCharacters.clear();
      this.usedSuggestions.clear();
      this.totalScriptLength = content.length;
      const lines = content.split('\n');
      
      // Debug logging for Meanwhile validation
      if (this.konamiService.serviceSelected === "aa_editor") {
        console.log('🔍 Tokenizing content with', lines.length, 'lines');
      }
      
      // Reset previous line type tracking
      this.previousLineType = 'other';
      this.previousCharacterTag = null;
      this.consecutiveTypewriterLines = 0;
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const isLastLine = i === lines.length - 1;
        const lineNumber = i + 1;
        
        // Track total lines (excluding empty lines)
        if (line.trim()) {
          this.totalLines++;
        }
        
        // Check if line is empty (only whitespace)
        if (!line.trim()) {
          // Preserve the original whitespace
          if (line.length > 0) {
            tokens.push({ text: line, type: 'normal' });
          }
          // Always add newline for empty lines to maintain scroll synchronization
          // This ensures the highlight div has the same height as the textarea
          tokens.push({ text: '\n', type: 'normal' });
          continue;
        }
        
        // Check for MEANWHILE line first (before other processing)
        const meanwhileMatch = line.match(/^(\s*)(Meanwhile)\s+([^:]+)$/i);
        if (meanwhileMatch) {
          const [, leadingWhitespace, meanwhileKeyword, restOfLine] = meanwhileMatch;
          
          // Debug logging for Meanwhile validation
          if (this.konamiService.serviceSelected === "aa_editor") {
            console.log('🎭 Meanwhile line detected:', {
              lineNumber,
              line: line.trim(),
              restOfLine,
              isFirstLine: i === 0 || (i > 0 && lines.slice(0, i).every(l => !l.trim()))
            });
          }
          
          // Check if this is the first non-empty line
          if (i === 0 || (i > 0 && lines.slice(0, i).every(l => !l.trim()))) {
            const errorMsg = 'The first line cant be a MEANWHILE line';
            this.errors.push({ text: 'Meanwhile', tooltip: errorMsg, line: lineNumber });
            
            // Add leading whitespace if any
            if (leadingWhitespace) {
              tokens.push({ text: leadingWhitespace, type: 'normal' });
            }
            
            tokens.push({ text: 'Meanwhile ', type: 'error' });
            
            // Process the rest of the line as normal
            const restTokens = this.processSystemCommands(restOfLine, lineNumber);
            tokens.push(...restTokens);
            
            // Always add newline to maintain scroll synchronization
            tokens.push({ text: '\n', type: 'normal' });
            continue;
          }
        }
        
        // Check for teleport command (e.g., "Teleport CHAR1 to Stage Center")
        const teleportMatch = line.match(/^(\s*)(Teleport)\s+([A-Z][A-Z0-9]*)\s+(to)\s+(.+)$/i);
        if (teleportMatch) {
          const [, leadingWhitespace, teleportCmd, characterTag, toKeyword, location] = teleportMatch;
          
          // Reset previous line type for non-dialog lines
          this.previousLineType = 'other';
          this.previousCharacterTag = null;
          
          // Add leading whitespace if any
          if (leadingWhitespace) {
            tokens.push({ text: leadingWhitespace, type: 'normal' });
          }
          
          // Check if character exists
          const character = this.characters.find(c => c.tag.toLowerCase() === characterTag.toLowerCase());
          
          tokens.push({ text: teleportCmd + ' ', type: 'teleport' });
          
          if (character) {
            tokens.push({
              text: characterTag + ' ',
              type: 'character',
              tag: characterTag,
              tooltip: character.name
            });
          } else {
            const errorMsg = `Unknown character tag: ${characterTag}`;
            this.errors.push({ text: characterTag, tooltip: errorMsg, line: lineNumber });
            tokens.push({
              text: characterTag + ' ',
              type: 'error'
            });
          }
          
          tokens.push({ text: toKeyword + ' ', type: 'teleport' });
          
          // Validate location
          const isValidLocation = this.isValidLocation(location.trim());
          if (isValidLocation) {
            tokens.push({ text: location, type: 'normal' });
          } else {
            const errorMsg = `Invalid location: ${location.trim()}`;
            this.errors.push({ text: location.trim(), tooltip: errorMsg, line: lineNumber });
            tokens.push({ text: location, type: 'error' });
          }
          
          // Always add newline to maintain scroll synchronization
          tokens.push({ text: '\n', type: 'normal' });
          continue;
        }
        
        // Check for character dialog (e.g., "DETECTIVE: Hello world" or "Meanwhile DETECTIVE: Hello world" or "GUEST 1337: Hello")
        const characterMatch = line.match(/^(\s*)((?:Meanwhile\s+)?[^:]+?)(\s*):\s*(.+)$/i);
        if (characterMatch) {
          const [, leadingWhitespace, characterName, trailingSpace, dialog] = characterMatch;
          const trimmedCharacterName = characterName.trim();
          
          // Debug logging for character dialog validation
          if (this.konamiService.serviceSelected === "aa_editor") {
            console.log('💬 Character dialog line detected:', {
              lineNumber,
              line: line.trim(),
              characterName: trimmedCharacterName,
              dialog: dialog.trim()
            });
          }
          
          // Check if this is a "meanwhile" line
          const meanwhileMatch = trimmedCharacterName.match(/^Meanwhile\s+(.+)$/i);
          const isMeanwhile = !!meanwhileMatch;
          const actualCharacterName = isMeanwhile ? meanwhileMatch![1].trim() : trimmedCharacterName;
          
          // Debug logging for Meanwhile dialog detection
          if (this.konamiService.serviceSelected === "aa_editor" && isMeanwhile) {
            console.log('🎭 Meanwhile dialog detected:', {
              lineNumber,
              originalCharacterName: trimmedCharacterName,
              actualCharacterName,
              dialog: dialog.trim()
            });
          }
          
          // Add leading whitespace if any
          if (leadingWhitespace) {
            tokens.push({ text: leadingWhitespace, type: 'normal' });
          }
          
          // Validate meanwhile usage
          if (isMeanwhile) {
            let hasError = false;
            
            // Check for adjacent meanwhile lines
            if (this.previousLineType === 'meanwhile') {
              const errorMsg = 'You can\'t have multiple "meanwhile" lines in a row. It\'s meant for basic actions between two characters at most. This might make it hard to have lots of characters do something at once... sorry about that';
              this.errors.push({ text: 'Meanwhile', tooltip: errorMsg, line: lineNumber });
              hasError = true;
            }
            
            // Check if meanwhile is used under sound effect/system line
            if (this.previousLineType === 'other') {
              const errorMsg = '"Meanwhile" should not be used after sound effects or system commands. It should follow dialogue lines.';
              this.errors.push({ text: 'Meanwhile', tooltip: errorMsg, line: lineNumber });
              hasError = true;
            }
            
            // Check if character tag is the same as previous dialog line
            if (this.previousCharacterTag && this.previousLineType === 'dialog') {
              const character = this.characters.find(c => 
                c.tag.toLowerCase() === actualCharacterName.toLowerCase() || 
                c.name.toLowerCase() === actualCharacterName.toLowerCase()
              );
              if (character && character.tag.toLowerCase() === this.previousCharacterTag.toLowerCase()) {
                const errorMsg = `"Meanwhile" must be used with a different character. "${actualCharacterName}" is the same as the previous speaker.`;
                this.errors.push({ text: actualCharacterName, tooltip: errorMsg, line: lineNumber });
                hasError = true;
              }
            }
            
            // Set token type based on validation
            if (hasError) {
              tokens.push({ text: 'Meanwhile ', type: 'error' });
            } else {
              tokens.push({ text: 'Meanwhile ', type: 'meanwhile' });
            }
            
            this.previousLineType = 'meanwhile';
          } else {
            this.previousLineType = 'dialog';
          }
          
          // Check if it's Typewriter (special case)
          if (actualCharacterName.toLowerCase() === 'typewriter') {
            // Track consecutive TYPEWRITER lines
            if (this.previousLineType === 'dialog' && this.previousCharacterTag === 'typewriter') {
              this.consecutiveTypewriterLines++;
            } else {
              this.consecutiveTypewriterLines = 1;
            }
            
            // Check for excessive consecutive TYPEWRITER lines
            if (this.consecutiveTypewriterLines > this.MAX_CONSECUTIVE_TYPEWRITER) {
              this.warnings.push({
                text: 'Typewriter',
                tooltip: `Many TYPEWRITER lines are used in a row, please do not do this, and instead have characters speak things out loud whenever possible.`,
                line: lineNumber
              });
            }
            
            tokens.push({
              text: actualCharacterName,
              type: 'typewriter',
              tooltip: 'Typewriter character'
            });
            // Add trailing space if it exists
            if (trailingSpace) {
              tokens.push({ text: trailingSpace, type: 'normal' });
            }
            tokens.push({ text: ': ', type: 'normal' });
            // Process dialog for suggestions
            const dialogTokens = this.processDialogForSuggestions(dialog, lineNumber);
            tokens.push(...dialogTokens);
            
            // Track character tag for meanwhile validation
            if (!isMeanwhile) {
              this.previousCharacterTag = 'typewriter';
            }
            
            // Check if this is an action-only line (only parentheses in dialog)
            if (this.isActionOnlyLine(dialog)) {
              this.actionOnlyLines++;
            }
          } else {
            // Check if it's a recognized character (only by tag, not by name)
            const character = this.characters.find(c => 
              c.tag.toLowerCase() === actualCharacterName.toLowerCase()
            );
            
            if (character) {
              tokens.push({
                text: actualCharacterName,
                type: 'character',
                tag: character.tag,
                tooltip: character.name
              });
              // Add trailing space if it exists
              if (trailingSpace) {
                tokens.push({ text: trailingSpace, type: 'normal' });
              }
              tokens.push({ text: ': ', type: 'normal' });
              
              // Validate dialogue length
              if (dialog.length > this.MAX_DIALOGUE_LENGTH) {
                const errorMsg = `A dialogue line is way too long-- it won't fit on the teleprompter! Limit is ${this.MAX_DIALOGUE_LENGTH} characters. Try splitting a line into several lines if you need to!`;
                this.errors.push({ text: "Line too long", tooltip: errorMsg, line: lineNumber });
              }
              
              // Process dialog for suggestions
              const dialogTokens = this.processDialogForSuggestions(dialog, lineNumber);
              tokens.push(...dialogTokens);
              
              // Track used character
              this.usedCharacters.add(character.tag);
              
              // Track character first appearance location
              if (!this.characterFirstAppearances.has(character.tag)) {
                // Access the location from the character object
                const locationName = (character as any).location?.name || 'Unknown';
                this.characterFirstAppearances.set(character.tag, locationName);
              }
              
              // Track character speaking lines
              const currentCount = this.characterSpeakingLines.get(character.tag) || 0;
              this.characterSpeakingLines.set(character.tag, currentCount + 1);
              
              // Track character tag for meanwhile validation
              if (!isMeanwhile) {
                this.previousCharacterTag = character.tag;
                this.consecutiveTypewriterLines = 0; // Reset TYPEWRITER counter for regular characters
              }
              
              // Check if this is an action-only line (only parentheses in dialog)
              if (this.isActionOnlyLine(dialog)) {
                this.actionOnlyLines++;
              }
            } else {
              const errorMsg = `${actualCharacterName} is not defined as a character. Make sure to use the correct character tags!`;
              this.errors.push({ text: actualCharacterName, tooltip: errorMsg, line: lineNumber });
              tokens.push({
                text: actualCharacterName,
                type: 'error'
              });
              // Add trailing space if it exists
              if (trailingSpace) {
                tokens.push({ text: trailingSpace, type: 'normal' });
              }
              tokens.push({ text: ': ', type: 'normal' });
              // Process dialog for suggestions
              const dialogTokens = this.processDialogForSuggestions(dialog, lineNumber);
              tokens.push(...dialogTokens);
              
              // Track character tag for meanwhile validation (even if unknown)
              if (!isMeanwhile) {
                this.previousCharacterTag = actualCharacterName;
                this.consecutiveTypewriterLines = 0; // Reset TYPEWRITER counter for unknown characters
              }
              
              // Check if this is an action-only line (only parentheses in dialog)
              if (this.isActionOnlyLine(dialog)) {
                this.actionOnlyLines++;
              }
            }
          }
          
          // Always add newline to maintain scroll synchronization
          tokens.push({ text: '\n', type: 'normal' });
          continue;
        }
        
        // Process system commands (sound effects, music, teleport, etc.)
        if (this.konamiService.serviceSelected === "aa_editor") {
          console.log('🔧 Processing as system command:', {
            lineNumber,
            line: line.trim()
          });
        }
        const systemTokens = this.processSystemCommands(line, lineNumber);
        tokens.push(...systemTokens);
        
        // Track non-dialogue lines
        this.nonDialogueLines++;
        
        // Note: Action-only lines are only checked for dialogue lines, not system commands
        // Sound effects in parentheses should not be counted as action-only lines
        
        // Reset previous line type for non-dialog lines
        this.previousLineType = 'other';
        this.previousCharacterTag = null;
        this.consecutiveTypewriterLines = 0;
        
        // Always add newline to maintain scroll synchronization
        tokens.push({ text: '\n', type: 'normal' });
      }
      // Persist the most recently processed content so other validators can read it
      this.lastProcessedContent = content;
      this.pendingContent = content;
      
      return tokens;
    });
  }
  
  private processSystemCommands(line: string, lineNumber: number): TextToken[] {
    return this.debugFunction('processSystemCommands', () => {
      const tokens: TextToken[] = [];
      
      // Debug logging for system command processing
      if (this.konamiService.serviceSelected === "aa_editor") {
        console.log('🔧 processSystemCommands called:', {
          lineNumber,
          line: line.trim()
        });
      }
      
      // Extract leading whitespace from the line
      const leadingWhitespaceMatch = line.match(/^(\s*)/);
      const leadingWhitespace = leadingWhitespaceMatch ? leadingWhitespaceMatch[1] : '';
      
      // Add leading whitespace if any
      if (leadingWhitespace) {
        tokens.push({ text: leadingWhitespace, type: 'normal' });
      }
      
      // Split by commas for multiple commands, but preserve the original command text
      const commands = line.split(',');
      
      for (let i = 0; i < commands.length; i++) {
        const command = commands[i];
        const trimmedCommand = command.trim();
        
        if (!trimmedCommand) continue;
        
        // Strip brackets from command for validation only
        let cleanCommand = trimmedCommand.replace(/[()\[\]{}]/g, '').trim();
        
        // Check for music stops command
        if (cleanCommand.toLowerCase() === 'music stops') {
          this.hasMusicCommands = true;
          tokens.push({ text: trimmedCommand, type: 'normal' });
          // Add comma if not the last command
          if (i < commands.length - 1) {
            tokens.push({ text: ',', type: 'normal' });
          }
          continue;
        }
        
        // Check for teleport command
        if (cleanCommand.toLowerCase().startsWith('teleport')) {
          const teleportMatch = cleanCommand.match(/^teleport\s+([A-Z][A-Z0-9]*)\s+to\s+(.+)$/i);
          if (teleportMatch) {
            const [, characterTag, location] = teleportMatch;
            
            // Check if character exists
            const character = this.characters.find(c => c.tag.toLowerCase() === characterTag.toLowerCase());
            if (!character) {
              const errorMsg = `Unknown character tag: ${characterTag}`;
              this.errors.push({ text: characterTag, tooltip: errorMsg, line: lineNumber });
              tokens.push({ text: trimmedCommand, type: 'error' });
              // Add comma if not the last command
              if (i < commands.length - 1) {
                tokens.push({ text: ',', type: 'normal' });
              }
              continue;
            }
            
            // Check if location is valid
            if (!this.isValidLocation(location.trim())) {
              const errorMsg = `Invalid location: ${location.trim()}`;
              this.errors.push({ text: location.trim(), tooltip: errorMsg, line: lineNumber });
              tokens.push({ text: trimmedCommand, type: 'error' });
              // Add comma if not the last command
              if (i < commands.length - 1) {
                tokens.push({ text: ',', type: 'normal' });
              }
              continue;
            }
            
            tokens.push({ text: trimmedCommand, type: 'normal' });
            // Add comma if not the last command
            if (i < commands.length - 1) {
              tokens.push({ text: ',', type: 'normal' });
            }
          } else {
            const errorMsg = 'Invalid TELEPORT command';
            this.errors.push({ text: 'teleport', tooltip: errorMsg, line: lineNumber });
            tokens.push({ text: trimmedCommand, type: 'error' });
            // Add comma if not the last command
            if (i < commands.length - 1) {
              tokens.push({ text: ',', type: 'normal' });
            }
          }
          continue;
        }
        
        // Check if it's a valid sound effect
        if (this.isValidSound(cleanCommand)) {
          // Check if it's a music command
          if (this.isMusicCommand(cleanCommand)) {
            this.hasMusicCommands = true;
          }
          tokens.push({ text: trimmedCommand, type: 'normal' });
          // Add comma if not the last command
          if (i < commands.length - 1) {
            tokens.push({ text: ',', type: 'normal' });
          }
          continue;
        }
        
        // If we get here, it's an invalid effect
        const errorMsg = `Invalid effect: "${cleanCommand}". If you're trying to do a sound effect, make sure it's spelled correctly`;
        
        // Debug logging for invalid effects
        if (this.konamiService.serviceSelected === "aa_editor") {
          console.log('❌ Invalid effect detected:', {
            lineNumber,
            cleanCommand,
            trimmedCommand,
            errorMsg
          });
        }
        
        this.errors.push({ text: cleanCommand, tooltip: errorMsg, line: lineNumber });
        tokens.push({ text: trimmedCommand, type: 'error' });
        // Add comma if not the last command
        if (i < commands.length - 1) {
          tokens.push({ text: ',', type: 'normal' });
        }
      }
      
      return tokens;
    });
  }
  
  private isValidSound(soundName: string): boolean {
    return this.debugFunction('isValidSound', () => {
      return this.sounds.some(sound => 
        sound.name.toLowerCase() === soundName.toLowerCase()
      );
    });
  }
  
  private isValidLocation(locationName: string): boolean {
    return this.debugFunction('isValidLocation', () => {
      // Check universal locations
      const isUniversal = this.universal_locations.some(location => 
        location.name.toLowerCase() === locationName.toLowerCase()
      );
      
      if (isUniversal) return true;
      
      // Check backdrop locations
      if (this.backdrop) {
        const isBackdropLocation = this.backdrop.locations.some(location => 
          location.name.toLowerCase() === locationName.toLowerCase()
        );
        
        if (isBackdropLocation) return true;
      }
      
      return false;
    });
  }
  
  private processDialogForSuggestions(dialog: string, lineNumber: number, isInAction: boolean = false): TextToken[] {
    return this.debugFunction('processDialogForSuggestions', () => {
      const tokens: TextToken[] = [];
      
      // Check for explicit words in dialogue
      this.checkForExplicitWords(dialog, lineNumber);
      
      // Check for improper action formatting
      this.checkActionFormatting(dialog, lineNumber);
      
      // Check for quotation marks in dialogue
      this.checkDialogueFormatting(dialog, lineNumber);
      
      // First, check for exact suggestion tag matches (including multi-word tags)
      let remainingText = dialog;
      
      // Sort suggestions by length (longest first) to match longer tags before shorter ones
      const sortedSuggestions = [...this.suggestions].sort((a, b) => b.tag.length - a.tag.length);
      
      while (remainingText.length > 0) {
        let matched = false;
        
        // Try to match each suggestion tag
        for (const suggestion of sortedSuggestions) {
          const tagIndex = remainingText.indexOf(suggestion.tag);
          if (tagIndex === 0) {
            // Found a suggestion tag at the beginning
            tokens.push({
              text: suggestion.tag,
              type: 'suggestion',
              tag: suggestion.tag,
              tooltip: suggestion.description
            });
            // Track used suggestion
            this.usedSuggestions.add(suggestion.tag);
            if (isInAction) {
              this.suggestionsUsedInActions.add(suggestion.tag);
            }
            
            remainingText = remainingText.substring(suggestion.tag.length);
            matched = true;
            break;
          }
        }
        
        if (!matched) {
          // Check for parentheses content first
          const parenthesesMatch = remainingText.match(/^\(([^)]*)\)/);
          if (parenthesesMatch) {
            const fullMatch = parenthesesMatch[0];
            const content = parenthesesMatch[1];
            
            // Add opening parenthesis
            tokens.push({ text: '(', type: 'parentheses' });
            
            // Process the content inside parentheses (recursively)
            const contentTokens = this.processDialogForSuggestions(content, lineNumber, true);
            
            // Apply parentheses styling to normal text tokens inside parentheses
            const styledContentTokens = contentTokens.map(token => {
              if (token.type === 'normal') {
                return { ...token, type: 'parentheses' as const };
              }
              return token;
            });
            
            tokens.push(...styledContentTokens);
            
            // Add closing parenthesis
            tokens.push({ text: ')', type: 'parentheses' });
            
            remainingText = remainingText.substring(fullMatch.length);
            continue;
          }
          
          // No suggestion tag found, process the first character/word
          const firstChar = remainingText[0];
          
          // If it's a space or punctuation, add it as normal text
          if (firstChar.match(/\s|[^\w\s-]/)) {
            tokens.push({ text: firstChar, type: 'normal' });
            remainingText = remainingText.substring(1);
          } else {
            // Find the next word boundary
            const wordMatch = remainingText.match(/^(\w+)/);
            if (wordMatch) {
              const word = wordMatch[1];
              
              // First, check if this word is a suggestion example in preview mode
              if (this.isPreviewMode && this.isSuggestionExample(word)) {
                const suggestion = this.findSuggestionByExample(word);
                if (suggestion) {
                  tokens.push({
                    text: word,
                    type: 'suggestion',
                    tag: suggestion.tag,
                    tooltip: suggestion.description
                  });
                } else {
                  tokens.push({ text: word, type: 'normal' });
                }
              } else if (word.match(/^[A-Z][A-Z0-9-]{3,}$/)) {
                // Check if this word is a suggestion (uppercase word with more than 3 characters)
                const suggestion = this.suggestions.find(s => s.tag === word);
                
                if (suggestion) {
                  tokens.push({
                    text: word,
                    type: 'suggestion',
                    tag: word,
                    tooltip: suggestion.description
                  });
                  // Track used suggestion
                  this.usedSuggestions.add(word);
                  if (isInAction) {
                    this.suggestionsUsedInActions.add(word);
                  }
                } else {
                  // Check if this word matches a character tag - if so, don't warn about it
                  const character = this.characters.find(c => c.tag === word);
                  if (character) {
                    // It's a character tag, treat as normal text
                    tokens.push({ text: word, type: 'normal' });
                  } else {
                    // Unknown suggestion - mark as warning
                    const warningMsg = `Unknown suggestion: ${word}. All-caps text should only be used for suggestion tags.`;
                    this.warnings.push({ text: word, tooltip: warningMsg, line: lineNumber });
                    tokens.push({
                      text: word,
                      type: 'warning'
                    });
                  }
                }
              } else {
                // Normal text
                tokens.push({ text: word, type: 'normal' });
              }
              
              remainingText = remainingText.substring(word.length);
            } else {
              // Fallback: add the first character as normal text
              tokens.push({ text: firstChar, type: 'normal' });
              remainingText = remainingText.substring(1);
            }
          }
        }
      }
      
      return tokens;
    });
  }
  
  private renderToken(token: TextToken): string {
    // Don't debug this function as it's called very frequently
    const baseClass = 'token';
    const typeClass = `token-${token.type}`;
    
    return `<span class="${baseClass} ${typeClass}">${this.escapeHtml(token.text)}</span>`;
  }
  
  private escapeHtml(text: string): string {
    // Don't debug this function as it's called very frequently
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  onFocus() {
    // Handle focus event if needed
  }
  
  onBlur() {
    // Handle blur event if needed
  }

  @HostListener('window:resize')
  onWindowResize() {
    // Recalculate line numbers when window is resized
    setTimeout(() => {
      this.updateLineNumbers();
    }, 100); // Small delay to ensure layout is complete
  }

  private isSuggestionExample(word: string): boolean {
    if (this.isPreviewMode) {
      // Check if this word is an example in the current replacements
      for (const [tag, replacement] of this.suggestionReplacements.entries()) {
        if (replacement.example === word) {
          return true;
        }
      }
    }
    return this.suggestions.some(suggestion => 
      suggestion.examples && suggestion.examples.includes(word)
    );
  }
  
  private findSuggestionByExample(word: string): {tag: string, description: string, examples: string[]} | null {
    if (this.isPreviewMode) {
      // Check if this word is an example in the current replacements
      for (const [tag, replacement] of this.suggestionReplacements.entries()) {
        if (replacement.example === word) {
          const suggestion = this.suggestions.find(s => s.tag === tag);
          return suggestion || null;
        }
      }
    }
    return this.suggestions.find(suggestion => 
      suggestion.examples && suggestion.examples.includes(word)
    ) || null;
  }
  
  private checkForExplicitWords(text: string, lineNumber: number): void {
    const words = text.toLowerCase().split(/\s+/);
    
    for (const word of words) {
      // Remove punctuation for comparison
      const cleanWord = word.replace(/[^\w]/g, '');
      
      if (this.EXPLICIT_WORDS.includes(cleanWord)) {
        this.errors.push({
          text: word,
          tooltip: `Explicit language detected: "${word}". Please use appropriate language suitable for all audiences.`,
          line: lineNumber
        });
      }
    }
  }
  
  private checkAllCharactersStartBackstage(): void {
    if (this.characters.length === 0) return;
    
    // Check all defined characters, not just the ones that appear in dialogue
    const allStartBackstage = this.characters.every(character => {
      const locationName = (character as any).location?.name || 'Unknown';
      return locationName.toLowerCase() === 'backstage';
    });
    
    if (allStartBackstage) {
      this.warnings.push({
        text: 'All characters start Backstage',
        tooltip: 'Every character in your script starts at Backstage. Consider having some characters start in different locations to create more dynamic scenes.'
      });
    }
  }
  
  private isSymbolicTag(tag: string): boolean {
    // Check for patterns ending with digit and less than 5 characters long
    return tag.length < 5 && /\d$/.test(tag);
  }
  
  private checkCharacterSpeakingLines(): void {
    for (const [characterTag, lineCount] of this.characterSpeakingLines.entries()) {
      if (lineCount < 4) {
        this.warnings.push({
          text: characterTag,
          tooltip: `Character "${characterTag}" has only ${lineCount} speaking lines. Consider giving them more dialogue to make them more active in the script.`
        });
      }
    }
  }
  
  private checkActionFormatting(text: string, lineNumber: number): void {
    // Check for asterisks, brackets, or curly braces in dialogue
    const asteriskMatch = text.match(/\*[^*]+\*/);
    const bracketMatch = text.match(/\[[^\]]+\]/);
    const braceMatch = text.match(/\{[^}]+\}/);
    
    if (asteriskMatch) {
      this.errors.push({
        text: asteriskMatch[0],
        tooltip: `Actions should not use asterisks (*) for formatting. Use parentheses for actions instead.`,
        line: lineNumber
      });
    }
    
    if (bracketMatch) {
      this.errors.push({
        text: bracketMatch[0],
        tooltip: `Actions should not use brackets ([]) for formatting. Use parentheses for actions instead.`,
        line: lineNumber
      });
    }
    
    if (braceMatch) {
      this.errors.push({
        text: braceMatch[0],
        tooltip: `Actions should not use curly braces ({}) for formatting. Use parentheses for actions instead.`,
        line: lineNumber
      });
    }
  }
  
  private checkDialogueFormatting(text: string, lineNumber: number): void {
    // Check if the entire dialogue is wrapped in quotation marks
    const trimmedText = text.trim();
    const startsWithQuote = /^[""''`]/.test(trimmedText);
    const endsWithQuote = /[""''`]$/.test(trimmedText);
    
    if (startsWithQuote && endsWithQuote) {
      this.errors.push({
        text: trimmedText,
        tooltip: `Dialogue should not be wrapped in quotation marks. The character name and colon already indicate speech.`,
        line: lineNumber
      });
    }
  }
  
  private checkMissingDialogue(): void {
    if (this.totalLines === 0) return;
    
    // Only warn/error on action-only lines, not system commands
    if (this.actionOnlyLines > 3) {
      this.errors.push({
        text: 'Too many action-only lines',
        tooltip: `Script has ${this.actionOnlyLines} action-only lines. Scripts should have mostly dialogue with minimal action descriptions.`
      });
    } else if (this.actionOnlyLines > 0) {
      this.warnings.push({
        text: 'Some action-only lines',
        tooltip: `Script has ${this.actionOnlyLines} action-only lines. Consider adding more dialogue to make the script more engaging.`
      });
    }
  }
  
  private isActionOnlyLine(line: string): boolean {
    // Check if line contains only parentheses content (actions)
    const trimmedLine = line.trim();
    const actionPattern = /^\([^)]*\)$/;
    // Exclude Meanwhile lines from action-only lines
    if (trimmedLine.toLowerCase().startsWith('meanwhile')) {
      return false;
    }
    return actionPattern.test(trimmedLine);
  }
  
  private isMusicCommand(command: string): boolean {
    const musicKeywords = ['music', 'song', 'melody', 'tune', 'score', 'soundtrack'];
    const lowerCommand = command.toLowerCase();
    return musicKeywords.some(keyword => lowerCommand.includes(keyword));
  }
  
  private checkMusicCommands(): void {
    if (this.totalLines > 0 && !this.hasMusicCommands) {
      this.warnings.push({
        text: 'No music',
        tooltip: 'Script has no music commands. Consider adding background music to enhance the atmosphere and engagement.'
      });
    }
  }
}